import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowUp } from 'react-icons/fa';

export default function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const height = document.documentElement.scrollHeight - window.innerHeight;
      const progress = height > 0 ? (scrollY / height) * 100 : 0;
      
      setIsVisible(scrollY > 400);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' 
    });
  };

  // Position based on device
  const positionClass = isMobile 
    ? 'bottom-24 right-6' 
    : 'bottom-24 lg:bottom-8 right-6 lg:right-8';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{ duration: 0.2 }}
          className={`fixed ${positionClass} z-40 flex flex-col items-center gap-1`}
        >
          {/* Progress Ring */}
          <div className="relative">
            {/* Background ring */}
            <svg className="w-14 h-14 -rotate-90">
              <circle
                cx="28"
                cy="28"
                r="24"
                stroke="#e5e7eb"
                strokeWidth="3"
                fill="none"
                className="opacity-30"
              />
              {/* Progress ring */}
              <circle
                cx="28"
                cy="28"
                r="24"
                stroke="url(#scrollGradient)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 24}`}
                strokeDashoffset={`${2 * Math.PI * 24 * (1 - scrollProgress / 100)}`}
                className="transition-all duration-300"
              />
              <defs>
                <linearGradient id="scrollGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#E8960D" />
                  <stop offset="100%" stopColor="#f7a220" />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Button */}
            <button
              onClick={scrollToTop}
              className="absolute inset-0.5 bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center group"
              aria-label="Scroll to top"
            >
              <FaArrowUp className="text-lg group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
          
          {/* Tooltip */}
          <span className="text-[10px] text-gray-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Top
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}